import type { SavedViewFilters, SavedViewsListResponse } from '#services/api/saved-views.ts';

import { z } from 'zod';

import { getDateRangeSearchParamsSchema } from '#utils/dates.ts';
import { BASE_APPOINTMENT_TYPES } from './Filters/AppointmentTypes.utils.ts';

const idsArraySchema = z
  .array(z.string())
  .transform((value) => value.map(Number).filter((value) => value > 0));

const formPrioritySchema = z.union([
  z.literal('all'),
  z.literal('required'),
  z.literal('optional'),
]);

const homeTabSchema = z.union([
  z.literal('scheduling'),
  z.literal('forms'),
  z.literal('coordinate'),
]);

const searchParamsSchema = z.object({
  tab: homeTabSchema.catch('scheduling'),
  view: z.coerce.number().positive().optional().catch(undefined),
  providerIds: idsArraySchema,
  appointmentTypeIds: idsArraySchema,
  addressIds: idsArraySchema,
  bookingChannelIds: z
    .array(z.union([z.literal('staff'), z.literal('pss'), z.literal('import')]))
    .catch([]),
  baseAppointmentTypes: z.array(z.enum(BASE_APPOINTMENT_TYPES)).catch([]),
  assessmentTypeIds: idsArraySchema,
  formsPriority: formPrioritySchema.catch('all'),
});

export type HomeTab = z.infer<typeof searchParamsSchema>['tab'];

/**
 * Parses the search params from the URL. Since the home route renders the
 * appointments and forms drilldowns as nested routes, we need to carry the
 * "base" search params into those routes in order for the home page to
 * maintain its state while the dialogs are displayed.
 */
export function getParsedSearchParams(searchParams: URLSearchParams) {
  const result = searchParamsSchema.parse({
    tab: searchParams.get('tab'),
    view: searchParams.get('view'),
    providerIds: searchParams.getAll('pId'),
    appointmentTypeIds: searchParams.getAll('atId'),
    addressIds: searchParams.getAll('aId'),
    bookingChannelIds: searchParams.getAll('bcId'),
    baseAppointmentTypes: searchParams.getAll('bat'),
    assessmentTypeIds: searchParams.getAll('astId'),
    formsPriority: searchParams.get('fp'),
  });

  const dateRangeResult = getDateRangeSearchParamsSchema().parse({
    dp: searchParams.get('dp'),
    from: searchParams.get('from'),
    to: searchParams.get('to'),
  });

  return { ...result, ...dateRangeResult };
}

/**
 * Maps from URL search param keys to the `filters` object keys stored by the
 * API. Only filter-related params are included — `tab` and `view` are excluded
 * because `tabType` lives on the saved view record itself.
 */
const filterParamMap = {
  dp: 'datePreset',
  from: 'from',
  to: 'to',
  pId: 'providerIds',
  atId: 'appointmentTypeIds',
  aId: 'addressIds',
  bcId: 'bookingChannelIds',
  bat: 'baseAppointmentTypes',
  astId: 'assessmentTypeIds',
  fp: 'formsPriority',
} as const;

type FilterParam = keyof typeof filterParamMap;
type FilterKey = (typeof filterParamMap)[FilterParam];

const paramByFilterKey = Object.fromEntries(
  Object.entries(filterParamMap).map(([param, key]) => [key, param])
) as Record<FilterKey, FilterParam>;

/**
 * Serializes validated filter state into the `filters` object shape expected by
 * the saved views API. Only shared filters and those belonging to the active
 * tab are included — filters from the opposite tab are excluded to prevent
 * stale cross-tab values from being persisted.
 *
 * `searchParams` is only used to detect whether date params were explicitly set
 * — the actual values always come from `parsed`.
 *
 * Date handling:
 * - If `dp` (date preset) is present → stores `datePreset` only, omits `from`/`to`
 * - If explicit `from` and/or `to` are present (no `dp`) → stores whichever are set
 * - If neither is present → omits date fields entirely (page default applies on load)
 */
export function serializeFilters(options: {
  parsed: ReturnType<typeof getParsedSearchParams>;
  tabType: 'scheduling' | 'forms';
}): SavedViewFilters {
  const { parsed, tabType } = options;
  const filters: SavedViewFilters = {};

  if (parsed.datePreset) {
    filters.datePreset = parsed.datePreset;
  } else {
    filters.from = parsed.fromDateFormat;
    filters.to = parsed.toDateFormat;
  }

  if (parsed.providerIds.length > 0) filters.providerIds = parsed.providerIds.map(String);

  if (tabType === 'scheduling') {
    if (parsed.appointmentTypeIds.length > 0)
      filters.appointmentTypeIds = parsed.appointmentTypeIds.map(String);
    if (parsed.addressIds.length > 0) filters.addressIds = parsed.addressIds.map(String);
    if (parsed.bookingChannelIds.length > 0) filters.bookingChannelIds = parsed.bookingChannelIds;
    if (parsed.baseAppointmentTypes.length > 0)
      filters.baseAppointmentTypes = parsed.baseAppointmentTypes;
  }

  if (tabType === 'forms') {
    if (parsed.assessmentTypeIds.length > 0)
      filters.assessmentTypeIds = parsed.assessmentTypeIds.map(String);
    if (parsed.formsPriority !== 'all') filters.formsPriority = parsed.formsPriority;
  }

  return filters;
}

/**
 * Inverse of `serializeFilters` — takes the `filters` object from a saved view
 * and writes it back into URL search params. Clears existing filter params
 * first to prevent stale values from persisting.
 *
 * Date handling:
 * - If `datePreset` exists → writes `dp`, omits `from`/`to`
 * - If `from` and/or `to` exist (no preset) → writes whichever are set
 * - If neither exists → writes nothing (page default applies)
 */
export function applyFiltersToSearchParams(
  filters: SavedViewFilters,
  searchParams: URLSearchParams
): URLSearchParams {
  const mergedSearchParams = new URLSearchParams(searchParams);

  for (const param of Object.keys(filterParamMap)) {
    mergedSearchParams.delete(param);
  }

  if (filters.datePreset) {
    mergedSearchParams.set('dp', filters.datePreset);
  } else {
    if (filters.from) mergedSearchParams.set('from', filters.from);
    if (filters.to) mergedSearchParams.set('to', filters.to);
  }

  for (const [key, value] of Object.entries(filters)) {
    if (key === 'datePreset' || key === 'from' || key === 'to') continue;

    const param = paramByFilterKey[key as FilterKey];

    if (Array.isArray(value)) {
      for (const v of value) {
        mergedSearchParams.append(param, String(v));
      }
    } else {
      mergedSearchParams.set(param, String(value));
    }
  }

  return mergedSearchParams;
}

/**
 * Resolves the effective search params by merging saved view filters with
 * explicit URL overrides. When a `view` param is present, the view's stored
 * filters become the base, and any explicit filter params in the URL override
 * them. Non-filter params (like `view` itself) are retained.
 *
 * @param searchParams - The raw URL search params from the request
 * @param savedViewsData - All available saved views to look up the view ID
 */
export function getSearchParamsToParse(
  searchParams: URLSearchParams,
  savedViewsData: SavedViewsListResponse
) {
  let paramsToParse = new URLSearchParams(searchParams);

  if (searchParams.has('view')) {
    const viewId = Number(searchParams.get('view'));
    const view = savedViewsData.views.find((v) => v.id === viewId);

    if (view) {
      paramsToParse = applyFiltersToSearchParams(view.filters, searchParams);
      paramsToParse.set('tab', view.tabType);
    } else {
      paramsToParse.delete('view');
    }
  }

  return paramsToParse;
}

export function getReadinessMagicLinkSearchParams(searchParams: URLSearchParams) {
  const cleanedSearchParams = new URLSearchParams();
  // A campaign can address another supported tab via an explicit ?tab= on the token's nextUrl;
  // magic links land on Coordinate otherwise.
  cleanedSearchParams.set('tab', homeTabSchema.catch('coordinate').parse(searchParams.get('tab')));

  for (const [key, value] of searchParams) {
    if (key === 'mt' || key === 'tab') continue;

    cleanedSearchParams.append(key, value);
  }

  return cleanedSearchParams;
}

export function getReadinessMagicLinkSearchParamsFromNextUrl(options: {
  nextUrl: string;
  fallbackSearchParams: URLSearchParams;
}) {
  try {
    return getReadinessMagicLinkSearchParams(
      new URL(options.nextUrl, window.location.origin).searchParams
    );
  } catch {
    return getReadinessMagicLinkSearchParams(options.fallbackSearchParams);
  }
}
